import React from 'react';
import { Box, Typography, Card } from '@mui/material';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import ownerPic from '../../Media/owner-pic.png';

const ExperienceSection = () => {
  // Optimized viewport configuration
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false,
    rootMargin: "-50px 0px"
  });

  const controls = useAnimation();

  React.useEffect(() => {
    // Start animations immediately after the component mounts
    controls.start(inView ? "visible" : "hidden");
  }, [controls, inView]);

  // Simplified animation variants for faster rendering
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.1,
        staggerChildren: 0.15,
        when: "beforeChildren"
      }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.1, ease: "easeOut" }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95, x: -50 },
    visible: {
      opacity: 1,
      scale: 1,
      x: 0,
      transition: { 
        duration: 0.1, 
        type: "spring", 
        stiffness: 120 
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 5, x: 20 },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      transition: { 
        duration: 0.1, 
        delay: 0.1,
        type: "spring",
        damping: 15,
      }
    }
  };

  const textVariants = {
    hidden: { opacity: 0, x: 30 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { 
        duration: 0.5,
        delay: i * 0.15 + 0.3,
        ease: "easeOut"
      }
    })
  };

  return (
    <Box 
      component={motion.div}
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      sx={{ 
        width: '100%',
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 }
      }}
    >
      <Box sx={{ 
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        <Typography
          component={motion.div}
          variants={titleVariants}
          variant="h4"
          sx={{ 
            textAlign: 'left', 
            color: '#64B5F6',
            fontWeight: 'bold',
            mb: 6,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              bottom: -10,
              width: '80px',
              height: '4px',
              background: 'linear-gradient(to right, #64B5F6, transparent)',
              borderRadius: '2px'
            }
          }}
        >
          Experience the Differences
        </Typography>
        
        <Box 
          sx={{
            minHeight: { xs: 'auto', md: '500px' },
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { md: 6 },
            alignItems: { xs: 'center', md: 'stretch' },
            width: '100%'
          }}
        >
          {/* Image Container */}
          <Box 
            component={motion.div}
            variants={imageVariants}
            sx={{ 
              flex: { xs: '1 1 auto', md: '0 0 46%' }, 
              position: 'relative', 
              maxWidth: { xs: '100%', md: '600px' },
              height: { xs: '400px', md: '500px' },
              mb: { xs: 4, md: 0 }
            }}
          >
            <Box
              component={motion.img}
              src={ownerPic}
              alt="Daniella Di Marco"
              whileHover={{ 
                scale: 1.03,
              }}
              transition={{ duration: 0.5 }}
              sx={{
                height: '100%',
                width: 'auto',
                display: 'block',
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
            
            <Card
              component={motion.div}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                boxShadow: "0px 15px 25px rgba(0, 0, 0, 0.1)"
              }}
              sx={{
                position: 'absolute',
                bottom: {xs: '-50px', md: '0'},
                right: {xs: '0px', },
                bgcolor: 'rgba(100, 181, 246, 0.85)',
                color: 'white',
                p: 2,
                textAlign: 'left',
                width: { xs: '100%', md: '60%' },
                borderRadius: '0px 10px 0px 10px',
                maxHeight:'60%',
                transition: 'all 0.3s ease',
                boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)"
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Rahim Virani
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 1, fontSize: '0.9rem' }}>
                CEO | MORTGAGELINE.INC.
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  fontStyle: 'italic',
                  lineHeight: 1.6,
                  opacity: 0.9,
                  fontSize: '0.85rem'
                }}
              >
                "I am a long-time Calgarian with a passion for supporting our local businesses and restaurants. I understand the market in Calgary and have built a strong relationship with the community including entrepreneurs, philanthropists, and young families. As an expert in mortgages, lending, and finance, I look forward to answering your questions and finding solutions."
              </Typography>
            </Card>
          </Box>

          {/* Text Content */}
          <Box 
            component={motion.div}
            sx={{ 
              flex: { xs: '1 1 auto', md: '0 0 54%' }, 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box sx={{ 
              p: 3, 
              maxWidth: '600px',
              width: '100%'
            }}>
              {[
                "With over two decades of expertise in finance and accounting, I bring a deep understanding of the ever-evolving mortgage and real estate landscape to help you achieve your homeownership goals. Whether you’re a first-time buyer, investor, or seeking to refinance, I collaborate with trusted lenders to secure competitive rates and terms tailored to your long-term aspirations—not just immediate needs.",
                "My client-focused approach prioritizes a thorough assessment of your financial situation, ensuring clarity on available options while emphasizing strategies to navigate challenges like affordability, credit health, and market headwinds. By aligning solutions with your unique needs, I simplify the path to confident decision-making .",
                "Specializing in personalized mortgage guidance, I support diverse clients—from first-time buyers to seasoned investors—through every phase of their homeownership journey. With a commitment to transparency and long-term success, I aim to turn your dream into reality by combining tailored advice, stress-free processes, and a dedication to building futures filled with security and stability ."
              ].map((text, index) => (
                <Typography
                  key={index}
                  component={motion.div}
                  custom={index}
                  variants={textVariants}
                  sx={{
                    mb: 2,
                    lineHeight: 1.8,
                    fontSize: {
                      xs: '1rem',
                      md: '0.875rem',
                      lg: '1rem'
                    }
                  }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExperienceSection;
